import { useState } from 'react'
import type { DateRange } from 'react-day-picker'
import { ko } from 'date-fns/locale'
import { CalendarDays, FileSpreadsheet } from 'lucide-react'
import { reports } from '@/api/endpoints'
import { saveBlob } from '@/api/client'
import { usePoll } from '@/hooks/usePoll'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Err } from '@/components/Err'
import { Pager } from '@/components/Pager'
import { Empty, PageHead, Section, SectionCrumb } from '@/components/layout/Page'

// 실적(B-15) — 기록을 통합 문서로 받는 자리. 화면에 표를 그리지 않는 것은 그 표가
// 엑셀에서 더 잘 읽히고, 받은 사람이 자기 기준으로 다시 보기 때문이다.
//
// 고르는 단위는 날짜 구간이다(#359). 달은 그 구간을 부르는 한 가지 방법일 뿐이라
// 아래 목록의 한 줄도 결국 첫날과 끝날을 보낸다.
//
// 고를 수 있는 것은 **기록이 있는 날**뿐이다(#364). 표본이 없던 기간의 보고서는
// 사용량이 적었던 기간이 아니라 아무도 재지 않은 기간이고, 그 파일은 오늘의 노드를
// 그때 있었던 것처럼 적는다.

const PER_PAGE = 6
const RECORD_MS = 60_000

const two = (n: number) => String(n).padStart(2, '0')
/** yyyy-mm-dd. 백엔드가 받는 형태이고, 그 날이 어느 시각인지는 백엔드가 정한다. */
const day = (d: Date) => `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`
/** yyyy-mm-dd 를 그 날 0시의 Date 로. new Date('yyyy-mm-dd') 는 UTC 라 하루가 밀린다. */
const parseDay = (s: string) => {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y!, m! - 1, d!)
}
const label = (year: number, month: number) => `${year}년 ${month}월`

type Month = { key: string; label: string; from: string; to: string }

/** 기록이 덮는 달, 최근이 맨 앞. 한 달의 끝은 기록의 끝을 넘지 않는다. */
function recordedMonths(first: string, last: string): Month[] {
  const start = parseDay(first)
  const end = parseDay(last)
  const out: Month[] = []
  for (let d = new Date(end.getFullYear(), end.getMonth(), 1); d >= new Date(start.getFullYear(), start.getMonth(), 1); d.setMonth(d.getMonth() - 1)) {
    const year = d.getFullYear()
    const month = d.getMonth()
    const from = new Date(year, month, 1)
    const to = new Date(year, month + 1, 0)
    out.push({
      key: `${year}-${two(month + 1)}`,
      label: label(year, month + 1),
      from: day(from < start ? start : from),
      to: day(to > end ? end : to),
    })
  }
  return out
}

export default function Reports() {
  const record = usePoll(() => reports.usageRecord(), RECORD_MS, [])
  const first = record.data?.first_day
  const last = record.data?.last_day

  if (record.loading && !record.data) return <Screen>{null}</Screen>
  if (record.error) return <Screen><Err error={record.error} /></Screen>
  if (!first || !last) {
    return (
      <Screen>
        <Section title="구간">
          <Empty>아직 기록된 사용량이 없음</Empty>
        </Section>
      </Screen>
    )
  }
  return <Picker first={first} last={last} />
}

function Screen({ children }: { children: React.ReactNode }) {
  return (
    <>
      <SectionCrumb />
      <PageHead icon={<FileSpreadsheet className="size-5" />} title="실적" facts="할당 기준과 사용량" />
      {children}
    </>
  )
}

function Picker({ first, last }: { first: string; last: string }) {
  const firstDate = parseDay(first)
  const lastDate = parseDay(last)
  const months = recordedMonths(first, last)
  // 기본은 기록 안의 이번 달 — 기록이 그보다 늦게 시작했으면 시작한 날부터다.
  const monthStart = new Date(lastDate.getFullYear(), lastDate.getMonth(), 1)
  const [range, setRange] = useState<DateRange | undefined>({
    from: monthStart < firstDate ? firstDate : monthStart,
    to: lastDate,
  })
  const [open, setOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)

  const download = async (key: string, from: string, to: string) => {
    setBusy(key)
    setError(null)
    try {
      // 이름은 백엔드의 Content-Disposition 이 정한다. 이것은 그 머리가 없을 때의 이름이다.
      saveBlob(await reports.usageXlsx(from, to), `실적 ${from}~${to}.xlsx`)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(null)
    }
  }

  // 시작만 고른 동안에는 그날 하루가 구간이다 — 받을 수 없는 상태를 만들지 않는다.
  const from = range?.from ? day(range.from) : ''
  const to = range?.to ? day(range.to) : from
  const pages = Math.max(1, Math.ceil(months.length / PER_PAGE))
  const shown = months.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  return (
    <Screen>
      <Section title="구간">
        <div className="flex flex-wrap items-center gap-2">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="justify-start gap-2 tabular-nums">
                <CalendarDays className="size-4" />
                {from ? `${from} ~ ${to}` : '날짜 고르기'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="range"
                locale={ko}
                numberOfMonths={2}
                startMonth={firstDate}
                endMonth={lastDate}
                {...(range ? { selected: range } : {})}
                {...(range?.from ? { defaultMonth: range.from } : {})}
                onSelect={(next) => setRange(next)}
                disabled={{ before: firstDate, after: lastDate }}
                autoFocus
              />
            </PopoverContent>
          </Popover>
          <Button disabled={!from || busy !== null} onClick={() => void download('range', from, to)}>
            {busy === 'range' ? '만드는 중' : 'xlsx 받기'}
          </Button>
        </div>
        <Err error={error} />
      </Section>

      <Section title="월별 보고서">
        <div className="overflow-hidden rounded-lg border">
          {shown.map((m) => (
            <div
              key={m.key}
              className="flex items-center justify-between gap-3 border-b px-4 py-2.5 last:border-b-0"
            >
              <div className="min-w-0">
                <div className="text-[13px]">{m.label}</div>
                <div className="text-[12px] tabular-nums text-muted-foreground">
                  {m.from} ~ {m.to}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={busy !== null}
                onClick={() => void download(m.key, m.from, m.to)}
              >
                {busy === m.key ? '만드는 중' : 'xlsx 받기'}
              </Button>
            </div>
          ))}
        </div>
        <Pager page={page} pages={pages} onPage={setPage} className="pt-3" />
      </Section>
    </Screen>
  )
}
